import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Marzipano from 'marzipano';
import type { MarzipanoController } from '../context/MarzipanoControllerContext';
import { getSceneById } from '../data/tour.config';
import type { ViewParams } from '../data/types';
import { translate } from '../i18n';
import { createInfoHotspotElement } from '../lib/infoHotspot';
import { createLinkHotspotElement, hotspotLabel } from '../lib/linkHotspot';
import { createMarzipanoScene, DEFAULT_FOV, getSceneTileConfig } from '../lib/marzipanoScene';
import { crossfadeSceneTransition } from '../lib/marzipanoTilePresentation';
import { bindSceneTileFade, installMarzipanoTileFade } from '../lib/marzipanoTileFade';
import { DeviceOrientationControlMethod } from '../lib/deviceOrientationControl';
import { useTourStore } from './useTourStore';

const SCENE_TRANSITION_MS = 700;
const GYRO_METHOD_ID = 'deviceOrientation';
const ZOOM_STEP = 0.16;
const PAN_STEP = 0.2;
const VIEW_TRANSITION_MS = 250;

function safeDestroyScene(viewer: Marzipano.Viewer, scene: Marzipano.Scene | null) {
  if (!scene) return;
  try {
    viewer.destroyScene(scene);
  } catch {
    // Scene may already be removed during React StrictMode double-unmount
  }
}

export function useMarzipano(containerRef: React.RefObject<HTMLDivElement | null>) {
  const viewerRef = useRef<Marzipano.Viewer | null>(null);
  const sceneRef = useRef<Marzipano.Scene | null>(null);
  const hotspotsRef = useRef<Marzipano.Hotspot[]>([]);
  const hotspotContainerRef = useRef<Marzipano.HotspotContainer | null>(null);
  const gyroRef = useRef<DeviceOrientationControlMethod | null>(null);
  const currentSceneIdRef = useRef<string | null>(null);
  const initialViewRef = useRef<ViewParams | null>(null);
  const autorotateRef = useRef<ReturnType<typeof Marzipano.autorotate> | null>(null);
  const autorotateEnabledRef = useRef(false);
  const mountedRef = useRef(true);
  const scenesToDestroyRef = useRef<Marzipano.Scene[]>([]);
  const unwatchTilesRef = useRef<(() => void) | null>(null);
  const [viewerReady, setViewerReady] = useState(0);
  const setAutorotateEnabled = useTourStore((s) => s.setAutorotating);
  const currentSceneId = useTourStore((s) => s.currentSceneId);
  const locale = useTourStore((s) => s.locale);
  const isGyroEnabled = useTourStore((s) => s.isGyroEnabled);
  const goToScene = useTourStore((s) => s.goToScene);

  const stopAutorotate = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    viewer.stopMovement();
    viewer.setIdleMovement(Number.POSITIVE_INFINITY);
    autorotateEnabledRef.current = false;
    setAutorotateEnabled(false);
  }, [setAutorotateEnabled]);

  const startAutorotate = useCallback(() => {
    const viewer = viewerRef.current;
    const movement = autorotateRef.current;
    if (!viewer || !movement) return;
    viewer.startMovement(movement);
    viewer.setIdleMovement(3000, movement);
    autorotateEnabledRef.current = true;
    setAutorotateEnabled(true);
  }, [setAutorotateEnabled]);

  const clearHotspots = useCallback(() => {
    const container = hotspotContainerRef.current;
    if (!container) {
      hotspotsRef.current = [];
      return;
    }

    [...hotspotsRef.current].forEach((hotspot) => {
      try {
        if (container.hasHotspot(hotspot)) {
          container.destroyHotspot(hotspot);
        }
      } catch {
        // ignore
      }
    });
    hotspotsRef.current = [];
  }, []);

  const attachHotspots = useCallback(
    (scene: Marzipano.Scene, sceneId: string, onNavigate: (id: string) => void) => {
      const config = getSceneById(sceneId);
      if (!config) return;

      clearHotspots();
      const container = scene.hotspotContainer();
      hotspotContainerRef.current = container;

      config.hotspots.forEach((hotspot) => {
        const label = hotspotLabel(locale, hotspot);
        const targetScene = getSceneById(hotspot.targetId);
        const previewSrc = targetScene
          ? getSceneTileConfig(targetScene).previewPath
          : undefined;
        const el = createLinkHotspotElement({
          label,
          previewSrc,
          onClick: () => onNavigate(hotspot.targetId),
        });

        const created = container.createHotspot(el, {
          yaw: hotspot.yaw,
          pitch: hotspot.pitch,
        });
        hotspotsRef.current.push(created);
      });

      config.infoHotspots?.forEach((info) => {
        const el = createInfoHotspotElement({
          title: translate(locale, info.titleKey),
          text: translate(locale, info.textKey),
          closeLabel: translate(locale, 'app.close'),
        });
        hotspotsRef.current.push(
          container.createHotspot(el, { yaw: info.yaw, pitch: info.pitch })
        );
      });
    },
    [clearHotspots, locale]
  );

  const loadScene = useCallback(
    (sceneId: string, onNavigate: (id: string) => void) => {
      const viewer = viewerRef.current;
      if (!viewer || !mountedRef.current) return;

      const config = getSceneById(sceneId);
      if (!config) return;

      stopAutorotate();
      clearHotspots();
      hotspotContainerRef.current = null;

      unwatchTilesRef.current?.();
      unwatchTilesRef.current = null;

      if (sceneRef.current) {
        scenesToDestroyRef.current.push(sceneRef.current);
      }

      const newScene = createMarzipanoScene(viewer, config);
      unwatchTilesRef.current = bindSceneTileFade(newScene, viewer);
      sceneRef.current = newScene;
      currentSceneIdRef.current = sceneId;
      initialViewRef.current = { ...config.initialView };

      newScene.switchTo(
        {
          transitionDuration: SCENE_TRANSITION_MS,
          transitionUpdate: crossfadeSceneTransition,
        },
        () => {
          if (!mountedRef.current || currentSceneIdRef.current !== sceneId) {
            safeDestroyScene(viewer, newScene);
            return;
          }

          scenesToDestroyRef.current.forEach((scene) => safeDestroyScene(viewer, scene));
          scenesToDestroyRef.current = [];

          attachHotspots(newScene, sceneId, onNavigate);
          startAutorotate();
        }
      );
    },
    [attachHotspots, clearHotspots, startAutorotate, stopAutorotate]
  );

  const navigateToScene = useCallback(
    (targetId: string) => {
      if (targetId === currentSceneIdRef.current) return;
      goToScene(targetId);
    },
    [goToScene]
  );

  const controller = useMemo<MarzipanoController>(
    () => ({
      zoomIn: () => {
        const scene = sceneRef.current;
        if (!scene) return;
        scene.view().offsetFov(-ZOOM_STEP);
      },
      zoomOut: () => {
        const scene = sceneRef.current;
        if (!scene) return;
        scene.view().offsetFov(ZOOM_STEP);
      },
      pan: (direction) => {
        const scene = sceneRef.current;
        if (!scene) return;
        stopAutorotate();
        const view = scene.view();
        if (direction === 'left') view.offsetYaw(-PAN_STEP);
        if (direction === 'right') view.offsetYaw(PAN_STEP);
        if (direction === 'up') view.offsetPitch(-PAN_STEP);
        if (direction === 'down') view.offsetPitch(PAN_STEP);
      },
      toggleAutorotate: () => {
        if (autorotateEnabledRef.current) {
          stopAutorotate();
        } else {
          startAutorotate();
        }
      },
      resetView: () => {
        const scene = sceneRef.current;
        const initialView = initialViewRef.current;
        if (!scene || !initialView) return;
        stopAutorotate();
        scene.view().setParameters(initialView, { transitionDuration: VIEW_TRANSITION_MS });
      },
      isAutorotateEnabled: () => autorotateEnabledRef.current,
    }),
    [startAutorotate, stopAutorotate]
  );

  useEffect(() => {
    mountedRef.current = true;
    const container = containerRef.current;
    if (!container) return;

    installMarzipanoTileFade();

    const viewer = new Marzipano.Viewer(container, {
      controls: { mouseViewMode: 'drag' },
    });
    viewerRef.current = viewer;

    autorotateRef.current = Marzipano.autorotate({
      yawSpeed: 0.03,
      targetPitch: 0,
      targetFov: DEFAULT_FOV,
    });

    const gyro = new DeviceOrientationControlMethod();
    gyroRef.current = gyro;
    viewer.controls().registerMethod(GYRO_METHOD_ID, gyro);
    setViewerReady((n) => n + 1);

    return () => {
      mountedRef.current = false;
      unwatchTilesRef.current?.();
      unwatchTilesRef.current = null;
      stopAutorotate();
      sceneRef.current = null;
      currentSceneIdRef.current = null;
      initialViewRef.current = null;
      gyroRef.current = null;
      hotspotContainerRef.current = null;
      hotspotsRef.current = [];
      scenesToDestroyRef.current = [];
      autorotateRef.current = null;

      try {
        viewer.controls().disableMethod(GYRO_METHOD_ID);
        viewer.destroy();
      } catch {
        // Viewer DOM may already be gone (React StrictMode)
      }

      viewerRef.current = null;
    };
  }, [containerRef, stopAutorotate]);

  useEffect(() => {
    if (!currentSceneId || !viewerRef.current || viewerReady === 0) return;

    if (currentSceneIdRef.current === currentSceneId && sceneRef.current) {
      attachHotspots(sceneRef.current, currentSceneId, navigateToScene);
      return;
    }

    loadScene(currentSceneId, navigateToScene);
  }, [currentSceneId, viewerReady, locale, loadScene, attachHotspots, navigateToScene]);

  useEffect(() => {
    const viewer = viewerRef.current;
    const gyro = gyroRef.current;
    const scene = sceneRef.current;
    if (!viewer || !gyro) return;

    const controls = viewer.controls();

    if (isGyroEnabled && scene) {
      const view = scene.view();
      gyro.getPitch((_err, pitch) => {
        view.setParameters({ pitch });
      });
      controls.enableMethod(GYRO_METHOD_ID);
    } else {
      controls.disableMethod(GYRO_METHOD_ID);
    }
  }, [isGyroEnabled, currentSceneId]);

  return { navigateToScene, controller };
}
